import type { ItemPrice } from "~~/shared/types/item.interfaces";

export const useItemsController = () => {
  const userStore = useUserStore();
  const { $serverApi } = useNuxtApp();
  const { web3Sign } = useWeb3();

  const getItems = async (merchantId: string) => {
    const res = await $serverApi<GFALResponse<Items | null>>(
      `/api/merchants/${merchantId}/items`,
      {
        headers: constructHeaders(userStore.authToken),
      }
    );
    if (!res.payload) return { records: [], total: 0 };
    return res.payload;
  };

  const getItemsByCategory = async (
    merchantId: string,
    categoryId: string,
    page: number,
    pageSize: number
  ) => {
    const res = await $serverApi<GFALResponse<Items | null>>(
      `/api/merchants/${merchantId}/items`,
      {
        headers: constructHeaders(userStore.authToken),
        query: {
          categoryId,
          page,
          pageSize,
        },
      }
    );
    if (!res.payload) return { records: [], total: 0, page: 0, totalPages: 0 };
    return res.payload;
  };

  const getConsumableItems = async (merchantId: string) => {
    const res = await $serverApi<GFALResponse<Items | null>>(
      `/api/merchants/${merchantId}/items`,
      {
        headers: constructHeaders(userStore.authToken),
        query: {
          type: "CONSUMABLE",
        },
      }
    );
    if (!res.payload) return { records: [], total: 0 };
    return res.payload;
  };

  const getCollectibleItems = async (merchantId: string) => {
    const res = await $serverApi<GFALResponse<Items | null>>(
      `/api/merchants/${merchantId}/items`,
      {
        headers: constructHeaders(userStore.authToken),
        query: {
          type: "COLLECTIBLE",
        },
      }
    );
    if (!res.payload) return { records: [], total: 0 };
    return res.payload;
  };

  const getItemById = async (itemId: string, merchantId: string) => {
    const res = await $serverApi<GFALResponse<Item | null>>(
      `/api/merchants/${merchantId}/items/${itemId}`,
      {
        headers: constructHeaders(userStore.authToken),
      }
    );
    if (!res.payload) return;
    return res.payload;
  };

  const getItemPrice = async (
    queuePosition: number,
    itemId: string,
    merchantId: string
  ) => {
    return await $serverApi<GFALResponse<ItemPrice>>(
      `/api/merchants/${merchantId}/items/${itemId}/price`,
      {
        method: "GET",
        headers: constructHeaders(userStore.authToken),
        query: { queuePosition },
      }
    );
  };

  const create = async (identifier: Identifier, code?: string) => {
    const { merchantId, id, price } = identifier as ItemIdentifier;
    return await $serverApi<GFALResponse<Purchase>>(
      `/api/merchants/${merchantId}/purchases`,
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
        body: {
          itemId: id,
          price,
          ...(code ? { code } : {}),
        },
      }
    );
  };

  const previewCreate = async (identifier: Identifier) => {
    const { merchantId, id, price } = identifier as ItemIdentifier;
    return await $serverApi<GFALResponse<Purchase>>(
      `/api/merchants/${merchantId}/purchases`,
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
        body: {
          itemId: id,
          price,
          preview: true,
        },
      }
    );
  };

  const sign = async (
    identifier: Identifier,
    purchaseId: string,
    operationHash: string,
    code: string | undefined
  ) => {
    const { merchantId } = identifier as ItemIdentifier;
    const signature = await web3Sign(operationHash);
    return await $serverApi<GFALResponse<Purchase>>(
      `/api/merchants/${merchantId}/purchases/${purchaseId}/sign`,
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
        body: {
          signature,
          code,
        },
      }
    );
  };

  return {
    getItems,
    getItemsByCategory,
    getConsumableItems,
    getCollectibleItems,
    getItemById,
    getItemPrice,
    previewCreate,
    create,
    sign,
  };
};
